import * as i2 from '@angular/common/http';
import { HttpParams, HttpRequest, HttpEventType, HttpHeaders, HttpClient } from '@angular/common/http';
import { timeout, filter, catchError, map } from 'rxjs/operators';
import { throwError, BehaviorSubject } from 'rxjs';
import { randomString } from '@appbolaget/helpers';
import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, NgModule } from '@angular/core';

var AwareHttpRequestMethod;
(function (AwareHttpRequestMethod) {
    AwareHttpRequestMethod["Get"] = "GET";
    AwareHttpRequestMethod["Post"] = "POST";
    AwareHttpRequestMethod["Put"] = "PUT";
    AwareHttpRequestMethod["Patch"] = "PATCH";
    AwareHttpRequestMethod["Delete"] = "DELETE";
})(AwareHttpRequestMethod || (AwareHttpRequestMethod = {}));
var AwareHttpRequestModule;
(function (AwareHttpRequestModule) {
    AwareHttpRequestModule["Cms"] = "cms";
    AwareHttpRequestModule["Broadcast"] = "broadcast";
    AwareHttpRequestModule["Iot"] = "iot";
    AwareHttpRequestModule["Media"] = "media";
    AwareHttpRequestModule["ProjectModule"] = "project";
    AwareHttpRequestModule["Transaction"] = "transaction";
    AwareHttpRequestModule["Universal"] = "universal";
})(AwareHttpRequestModule || (AwareHttpRequestModule = {}));

class AwareHttpRequest {
    constructor(api) {
        this.api = api;
        this._params = new HttpParams();
        this._responseType = 'json';
        this._headers = this.api.headers;
    }
    addSubRequest(subRequest, key) {
        if (!this._subRequests) {
            this._subRequests = new Map();
        }
        this._subRequests.set(key || randomString(), subRequest);
    }
    body(body) {
        this._body = body;
        return this;
    }
    clone() {
        const clone = new AwareHttpRequest(this.api).url(this._url).body(this._body).method(this._method);
        for (const key of this._params.keys()) {
            clone.parameter(key, this._params.get(key));
        }
        for (const key of this._headers.keys()) {
            clone.header(key, this._headers.get(key));
        }
        return clone;
    }
    endpoint(path) {
        this.url(this.api._url(path));
        return this;
    }
    execute() {
        if (this.api.apiConfig.projectModule && this._headers.get('module') === AwareHttpRequestModule.ProjectModule) {
            this.module(this.api.apiConfig.projectModule);
        }
        // Manually set the token before each execute incase there has been a token refresh.
        // Still doesn't cover the case when request already have been executed but not yet subscribed to,
        // and then a refresh happens. Only viable option seems to be an interceptor.
        this._headers.set('token', this.api.headers.get('token'));
        const request = new HttpRequest(this._method, this._url, this._body ? this._body : null, {
            headers: this._headers,
            params: this._params,
            reportProgress: false,
            responseType: this._responseType,
        });
        return this.api.http.request(request).pipe(timeout(30000), filter((res) => res.type === HttpEventType.Response), catchError((err) => {
            return throwError(() => err);
        }), map((res) => {
            if (this._collectionModel) {
                const collection = {
                    next: res.body.pagination?.next || false,
                    page: parseInt(res.body.pagination?.page || 0),
                    data: res.body.data.map((m) => new this._collectionModel(m)),
                };
                return collection;
            }
            else if (this._model) {
                return new this._model(res.body.data);
            }
            return res.body;
        }));
    }
    expand(...expand) {
        if (Array.isArray(expand[0])) {
            expand = expand[0];
        }
        this._setParam('expand', expand.join(','));
        return this;
    }
    with(...args) {
        this._setParam('with', args.join(','));
        return this;
    }
    filter(filterData, stringify = true) {
        this._setParam('filter', stringify ? JSON.stringify(filterData) : filterData);
        return this;
    }
    header(key, value) {
        this._setHeader(key, value);
        return this;
    }
    headers(headers) {
        if (!headers) {
            return this;
        }
        for (const key in headers) {
            this._setHeader(key, headers[key]);
        }
        return this;
    }
    limit(limit) {
        this._setParam('limit', limit);
        return this;
    }
    method(method) {
        this._method = method;
        return this;
    }
    module(module) {
        this._setHeader('module', module);
        return this;
    }
    page(page) {
        this._setParam('page', page);
        return this;
    }
    parameter(key, value) {
        this._setParam(key, value);
        return this;
    }
    parameters(params) {
        for (const param in params) {
            if (params[param]) {
                this._setParam(param, params[param]);
            }
        }
        return this;
    }
    responseType(type = 'json') {
        this._responseType = type;
        return this;
    }
    sort(key, value) {
        this._setParam('sort', JSON.stringify({ [key]: value }));
        return this;
    }
    stream(dir) {
        this._setParam('stream', dir);
        return this;
    }
    toCollection(model) {
        this._collectionModel = model;
        return this;
    }
    toModel(model) {
        this._model = model;
        return this;
    }
    token(token) {
        this._setHeader('token', token);
        return this;
    }
    type(...types) {
        this._setParam('type', types.length > 1 ? JSON.stringify(types) : types[0]);
        return this;
    }
    unit(uuid) {
        this._setHeader('unit', uuid);
        return this;
    }
    url(url) {
        this._url = url;
        return this;
    }
    getBody() {
        return this._body;
    }
    getHeaders() {
        return this._headers;
    }
    getMethod() {
        return this._method;
    }
    getParams() {
        return this._params;
    }
    getSubRequests() {
        return this._subRequests;
    }
    getUrl() {
        return this._url;
    }
    _setHeader(key, value) {
        if (value) {
            this._headers = this._headers.set(key.toLowerCase(), value);
        }
        else if (this._headers.has(key.toLowerCase())) {
            this._headers = this._headers.delete(key.toLowerCase());
        }
    }
    _setParam(key, value) {
        if (value) {
            this._params = this._params.set(key.toLowerCase(), value);
        }
        else {
            this._params = this._params.delete(key.toLowerCase());
        }
    }
}

const FOR_ROOT_OPTIONS = new InjectionToken('forRoot() ApiModule configuration');
class AwareHttpConfigService {
    constructor() { }
    get basePath() {
        return this.options.basePath;
    }
    get devPath() {
        return this.options.devPath;
    }
    get defaultHeaders() {
        return this.options.defaultHeaders;
    }
    get projectModule() {
        return this.options.projectModule;
    }
    setOptions(options) {
        this.options = options;
    }
}
AwareHttpConfigService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: AwareHttpConfigService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
AwareHttpConfigService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: AwareHttpConfigService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: AwareHttpConfigService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return []; } });

class AwareHttpService {
    constructor(http, apiConfig) {
        this.http = http;
        this.apiConfig = apiConfig;
        this.isDevMode = new BehaviorSubject(false);
        this.isDevMode$ = this.isDevMode.asObservable();
        this.headers = this._setDefaultHeaders();
    }
    batch(request) {
        return (new AwareHttpRequest(this)
            .url(this._url('batch'))
            .body({ request: JSON.stringify(request) })
            // .body(Helpers.encodeElement({ request: JSON.stringify(request) }))
            // .header('Content-Type', 'application/x-www-form-urlencoded;charset=utf-8')
            .method(AwareHttpRequestMethod.Post));
    }
    batchRequests(requests) {
        const batch = {};
        const self = this;
        function parseRequest(request, key) {
            const route = request.getUrl().split(self.isDevMode.value ? self.apiConfig.devPath : self.apiConfig.basePath)[1];
            const headers = request
                .getHeaders()
                .keys()
                .reduce((acc, curr) => {
                if (curr.toLowerCase() === 'token') {
                    return acc;
                }
                acc[curr] = request.getHeaders().get(curr);
                return acc;
            }, {});
            const query = request
                .getParams()
                .keys()
                .reduce((acc, curr) => {
                acc[curr] = request.getParams().get(curr);
                return acc;
            }, {});
            const body = request.getBody();
            if (body?.meta && typeof body?.meta === 'string') {
                body.meta = JSON.parse(body.meta);
            }
            const requestObject = {
                name: key,
                route,
                method: request.getMethod(),
                headers,
                query,
                data: body,
            };
            const subRequests = request.getSubRequests();
            if (subRequests?.size) {
                requestObject.requests = [...subRequests].map(([key, req]) => parseRequest(req, key));
            }
            return requestObject;
        }
        for (const key in requests) {
            const request = requests[key];
            batch[key] = parseRequest(request, key);
        }
        return this.batch(batch);
    }
    delete(endpoint, body, id) {
        endpoint = id ? [endpoint, id].join('/') : endpoint;
        return new AwareHttpRequest(this).url(this._url(endpoint)).body(body).method(AwareHttpRequestMethod.Delete);
    }
    get(endpoint) {
        return new AwareHttpRequest(this).url(this._url(endpoint)).method(AwareHttpRequestMethod.Get);
    }
    post(endpoint, body) {
        return new AwareHttpRequest(this).url(this._url(endpoint)).body(body).method(AwareHttpRequestMethod.Post);
    }
    put(endpoint, body, id) {
        endpoint = id ? [endpoint, id].join('/') : endpoint;
        return new AwareHttpRequest(this).url(this._url(endpoint)).body(body).method(AwareHttpRequestMethod.Put);
    }
    show(endpoint, id) {
        return this.get([endpoint, id].join('/'));
    }
    upload(formData, endpoint = 'files') {
        return new AwareHttpRequest(this)
            .url(this._url(endpoint))
            .body(formData)
            .method(AwareHttpRequestMethod.Post)
            .module(AwareHttpRequestModule.Media)
            .expand('meta');
    }
    setDefaultHeader(key, value) {
        if (value) {
            this.headers = this.headers.set(key.toLowerCase(), value);
        }
        else if (this.headers.has(key.toLowerCase())) {
            this.headers = this.headers.delete(key.toLowerCase());
        }
    }
    setUnit(unit) {
        this.setDefaultHeader('unit', unit);
    }
    setToken(token) {
        this.setDefaultHeader('token', token);
    }
    hasDevMode() {
        return this.apiConfig.devPath ? true : false;
    }
    toggleDevMode(value = !this.isDevMode.value) {
        if (!this.apiConfig.devPath) {
            throw new Error('No dev path available');
        }
        this.isDevMode.next(value);
        return value;
    }
    _setDefaultHeaders() {
        const headers = new HttpHeaders({ ...this.apiConfig.defaultHeaders });
        return headers;
    }
    _url(url) {
        return [this.isDevMode.value ? this.apiConfig.devPath : this.apiConfig.basePath, url].join('/');
    }
}
AwareHttpService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: AwareHttpService, deps: [{ token: HttpClient }, { token: AwareHttpConfigService }], target: i0.ɵɵFactoryTarget.Injectable });
AwareHttpService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: AwareHttpService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: AwareHttpService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i2.HttpClient, decorators: [{
                    type: Inject,
                    args: [HttpClient]
                }] }, { type: AwareHttpConfigService }]; } });

class AwareHttpModule {
    static forRoot(options) {
        return {
            ngModule: AwareHttpModule,
            providers: [
                AwareHttpService,
                {
                    provide: FOR_ROOT_OPTIONS,
                    useValue: options,
                },
                {
                    provide: AwareHttpConfigService,
                    useFactory: provideHttpService,
                    deps: [FOR_ROOT_OPTIONS],
                },
            ],
        };
    }
}
AwareHttpModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: AwareHttpModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AwareHttpModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: AwareHttpModule });
AwareHttpModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: AwareHttpModule, providers: [AwareHttpService], imports: [[]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: AwareHttpModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [],
                    exports: [],
                    declarations: [],
                    providers: [AwareHttpService],
                }]
        }] });
function provideHttpService(options) {
    const service = new AwareHttpConfigService();
    service.setOptions(options);
    return service;
}

/*
 * Public API Surface of aware-http
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AwareHttpConfigService, AwareHttpModule, AwareHttpRequest, AwareHttpRequestMethod, AwareHttpRequestModule, AwareHttpService, FOR_ROOT_OPTIONS, provideHttpService };
